import { computed, defineComponent, getCurrentInstance, onMounted, reactive, ref, useCssModule, watch, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { OUTPUT_ARTICLES_LENGTH, TOP_ARTICLES_TYPES, TRAFFIC_ARTICLE_ALIAS } from '.';
import { ADFOX_FETCH_STATES } from '~/components/Adfox';
import { useDuck } from '~/services';
import { useRecommendationBanner } from '~/composables';
/**
 * Duck service.
 */
const duck = useDuck();
export default defineComponent({
    name: 'RecommendationBanner',
    props: {
        type: {
            default: 'article',
            type: String,
        },
    },
    emits: ['complete'],
    setup(props, { emit }) {
        /**
         * Composables
         */
        const banner = useRecommendationBanner();
        const css = useCssModule();
        const articles = ref([]);
        const indexes = new Set();
        const error = reactive({});
        const state = reactive({ ...ADFOX_FETCH_STATES });
        const outputArticlesLength = ref(OUTPUT_ARTICLES_LENGTH);
        const { uid } = getCurrentInstance();
        if (!banner.firstInstanceUid) {
            banner.firstInstanceUid = uid;
        }
        const isFirstInstance = computed(() => banner.firstInstanceUid === uid);
        /**
         * Fetch task
         */
        const fetchArticlesTask = useTask(function* (signal) {
            signal.addEventListener('abort', () => duck.abortRequest('getPubActual'));
            try {
                // Fetch and handle traffic article
                if (TRAFFIC_ARTICLE_ALIAS) {
                    // Fetch traffic article
                    if (!banner.trafficArticle) {
                        try {
                            const { data: trafficData } = yield duck.pub.getPubArticleByAlias({
                                url_alias: TRAFFIC_ARTICLE_ALIAS,
                            });
                            banner.trafficArticle = trafficData;
                        }
                        catch (e) {
                            throwError('There is no traffic article', e);
                        }
                    }
                    // Handle traffic article
                    if (banner.trafficArticle) {
                        outputArticlesLength.value -= 1;
                        articles.value.push(banner.trafficArticle);
                    }
                }
                // Fetch actual articles
                if (!banner.articles.length) {
                    const { data } = props.type === 'sport' ? yield duck.pub.getPubActualSport() : yield duck.pub.getPubActual();
                    state.isLoaded = Boolean(data.length);
                    if (!data.length) {
                        return throwError('There is no articles');
                    }
                    data.map((item) => (item.url_alias += '?utm_source=forbes&utm_campaign=aside-adblock'));
                    banner.articles = data;
                    banner.topArticles = data.filter(({ type }) => TOP_ARTICLES_TYPES.includes(type));
                }
                // Handle and randomize top articles
                if (!TRAFFIC_ARTICLE_ALIAS && banner.topArticles.length) {
                    outputArticlesLength.value -= 1;
                    banner.articles = banner.articles.filter(({ type }) => !TOP_ARTICLES_TYPES.includes(type));
                    if (banner.topArticlesLastIds.size >= banner.topArticles.length) {
                        const value = Array.from(banner.topArticlesLastIds)[0];
                        banner.topArticlesLastIds.delete(value);
                    }
                    for (const topArticle of banner.topArticles) {
                        if (banner.topArticlesLastIds.size === 0 || !banner.topArticlesLastIds.has(topArticle.id)) {
                            banner.topArticlesLastIds.add(topArticle.id);
                            articles.value.push(topArticle);
                            break;
                        }
                    }
                }
                if (banner.articles.length <= outputArticlesLength.value) {
                    state.isRendered = true;
                    articles.value = [...articles.value, ...banner.articles];
                    return;
                }
                randomizeIndexes(banner.articles.length);
                for (const index of indexes) {
                    articles.value.push(banner.articles[index]);
                }
                articles.value.length = OUTPUT_ARTICLES_LENGTH;
                state.isRendered = true;
            }
            catch (e) {
                throwError('', e);
            }
            finally {
                state.isCompleted = true;
                banner.isFirstInstanceCompleted = true;
                emit('complete', { error, state });
            }
        });
        /**
         * Randomizes indexes
         * @param length
         */
        function randomizeIndexes(length) {
            if (banner.lastIndexes.size >= length) {
                for (let i = 0; i < outputArticlesLength.value; i++) {
                    const index = Array.from(banner.lastIndexes)[i];
                    banner.lastIndexes.delete(index);
                }
            }
            while (indexes.size < outputArticlesLength.value) {
                fillIndexes(length);
            }
        }
        /**
         * Recursively fills indexes
         * @param length
         */
        function fillIndexes(length) {
            const index = Math.floor(Math.random() * length);
            if (banner.lastIndexes.size === 0 || !banner.lastIndexes.has(index)) {
                indexes.add(index);
                banner.lastIndexes.add(index);
            }
            else {
                fillIndexes(length);
            }
        }
        /**
         * Throws console error
         * @param message
         * @param payload
         */
        function throwError(message = '', payload = '') {
            state.withError = true;
            error.message = message;
            console.error('[RecommendationBanner]', message, payload);
        }
        onMounted(async () => {
            watch([() => isFirstInstance.value, () => banner.isFirstInstanceCompleted], ([isFirstInstance, isFirstInstanceCompleted]) => {
                if (isFirstInstance || isFirstInstanceCompleted) {
                    fetchArticlesTask.perform();
                }
            }, { immediate: true });
        });
        return {
            articles,
            banner,
            css,
            error,
            fetchArticlesTask,
            indexes,
            state,
        };
    },
});
